import React from 'react'

import { useDashboardFeatureFlags } from '@components/utils/DashboardFeatureFlags'
import { ConfirmModal, useModalContext } from '@fourthwall/components'
import {
  AudioIcon,
  PhotoIcon,
  PollIcon,
  StreamIcon,
  TypographyIcon,
  VideoIcon,
} from '@fourthwall/icons'
import { routing } from '@utils/routing'

import type { SuggestionProps } from '../../../../../../../../components/common/Suggestion'
import { addUtmSourceParamToUrl } from '../../../../../../../../utils/url'
import { NewPostLinks } from '../../../components/NewPostLinks/NewPostLinks'
import type { PostLink } from '../../../components/NewPostLinks/types'
import createNewPost from '../assets/create-a-new-post.png'
import membersOnlyDiscord from '../assets/members-only-discord.png'
import membersOnlyProducts from '../assets/members-only-products.png'
import membersOnlyPromotions from '../assets/members-only-promotions.png'
import setUpWelcomePost from '../assets/set-up-welcome-post.png'
import spotify from '../assets/spotify.png'
import twitchDiscounts from '../assets/twitch-discounts.png'
import { useSuggestionsDismiss } from './useSuggestionsDismiss'

export const POST_LINKS: PostLink[] = [
  {
    icon: TypographyIcon,
    type: 'Text',
  },
  {
    icon: PhotoIcon,
    type: 'Image',
  },
  {
    icon: VideoIcon,
    type: 'Video',
  },
  {
    icon: AudioIcon,
    type: 'Audio',
  },
  {
    icon: PollIcon,
    type: 'Poll',
  },
  {
    icon: StreamIcon,
    type: 'LiveStream',
  },
]

type Suggestion = {
  visible: boolean
  suggestionProps: SuggestionProps
}

export const useSuggestions = () => {
  const modal = useModalContext()
  const { suggestionsDismiss } = useSuggestionsDismiss()
  const { dashboardFeatureFlags } = useDashboardFeatureFlags()

  const createOnDismiss = (onConfirm: () => void) => () => {
    modal.open(ConfirmModal, {
      title: 'Are you sure you want to hide this recommendation?',
      text: 'This action cannot be reversed.',
      confirmLabel: 'Hide recommendation',
      onConfirm,
    })
  }

  const postLinks = POST_LINKS.filter((link) => {
    if (link.type === 'LiveStream') return dashboardFeatureFlags.membershipsLiveStream.enabled

    return true
  })

  const newPostSuggestion: Suggestion = {
    visible: suggestionsDismiss.newPost.visible,
    suggestionProps: {
      title: 'Create a new post',
      description: 'Add more content to engage with your members',
      content: (
        <NewPostLinks
          links={postLinks}
          getLinkHref={({ type }) => `${routing.memberships.posts.create}?type=${type}`}
        />
      ),
      imageUrl: createNewPost,
      onDismiss: createOnDismiss(suggestionsDismiss.newPost.dismiss),
    },
  }

  const welcomePostSuggestion: Suggestion = {
    visible: suggestionsDismiss.welcomePost.visible,
    suggestionProps: {
      title: 'Create welcome post',
      description: 'Create a post that appears to new members after they subscribe',
      imageUrl: setUpWelcomePost,
      buttonLabel: 'Create welcome post',
      buttonTo: routing.memberships.settings.welcomePost,
      onDismiss: createOnDismiss(suggestionsDismiss.welcomePost.dismiss),
    },
  }

  const discordSuggestion: Suggestion = {
    visible: suggestionsDismiss.discord.visible,
    suggestionProps: {
      title: 'Set up members-only Discord access',
      description: 'Grant members exclusive roles and access on Discord',
      imageUrl: membersOnlyDiscord,
      buttonLabel: 'Set up Discord perk',
      buttonTo: routing.memberships.perks.self,
      onDismiss: createOnDismiss(suggestionsDismiss.discord.dismiss),
    },
  }

  const productsSuggestion: Suggestion = {
    visible: suggestionsDismiss.products.visible,
    suggestionProps: {
      title: 'Offer members-only products',
      description: 'Add exclusive products that only members can buy',
      imageUrl: membersOnlyProducts,
      buttonLabel: 'Learn more',
      buttonHref: addUtmSourceParamToUrl(
        'https://help.fourthwall.com/hc/en-us/articles/13331279583387-Perks-A-Guide-to-Rewarding-Your-Members#members-only-products',
        'onboarding',
      ),
      buttonTarget: '_blank',
      onDismiss: createOnDismiss(suggestionsDismiss.products.dismiss),
    },
  }

  const promotionsSuggestion: Suggestion = {
    visible: suggestionsDismiss.promotions.visible,
    suggestionProps: {
      title: 'Offer members-only promotions',
      description: 'Let your members purchase selected products at a discount',
      imageUrl: membersOnlyPromotions,
      buttonLabel: 'Learn more',
      buttonHref: addUtmSourceParamToUrl(
        'https://help.fourthwall.com/hc/en-us/articles/13331279583387-Perks-A-Guide-to-Rewarding-Your-Members#members-only-discounts',
        'onboarding',
      ),
      buttonTarget: '_blank',
      onDismiss: createOnDismiss(suggestionsDismiss.promotions.dismiss),
    },
  }

  const twitchDiscountSuggestion: Suggestion = {
    visible: suggestionsDismiss.twitchDiscount.visible,
    suggestionProps: {
      title: 'Offer membership discounts for Twitch subs',
      description: 'Offer your Twitch subscribers discounts on membership tiers',
      imageUrl: twitchDiscounts,
      buttonLabel: 'Set up discounts for Twitch subs',
      buttonTo: routing.apps.twitchMembershipsDiscounts,
      onDismiss: createOnDismiss(suggestionsDismiss.twitchDiscount.dismiss),
    },
  }

  const spotifySuggestion: Suggestion = {
    visible: suggestionsDismiss.spotify.visible,
    suggestionProps: {
      title: 'Allow members to listen on Spotify',
      description: 'Sync your members-only audio to Spotify',
      imageUrl: spotify,
      buttonLabel: 'Sync to Spotify',
      buttonTo: routing.apps.spotify.self,
      onDismiss: createOnDismiss(suggestionsDismiss.spotify.dismiss),
    },
  }

  return {
    newPostSuggestion,
    welcomePostSuggestion,
    discordSuggestion,
    productsSuggestion,
    promotionsSuggestion,
    twitchDiscountSuggestion,
    spotifySuggestion,
  }
}
